import { ChallengeDoc, StepLog } from '@sworkit/api';
import { ChartDataset, ChartOptions } from 'chart.js';
import { formatDate, getDateFnsLocale, translate } from './translate';
import { formatDistanceToNow } from 'date-fns';
import {
  exhaustiveCheck,
  getConvertedDistanceForUnit,
  getDateAtMidnightFromString,
  getDistanceUnitTranslationKey,
  getTextColor,
  getTodayString,
} from './utils';
import { swApi } from './app';

export type DateSegment = 'week' | 'month' | 'year';
export type StepViewOption = 'steps' | 'distance';

export const stepViewOptions: StepViewOption[] = [
  'steps',
  'distance',
];

export function getChartOptions(
  viewOption: StepViewOption,
  distanceUnit: SworkitState['user']['profile']['distanceUnit'],
  min: number,
  max: number,
  dateSegment: DateSegment = 'week',
) {
  const options: ChartOptions = {
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        yAlign: 'bottom',
        backgroundColor: '#131b28',
        callbacks: {
          label: (tooltipItem) => {
            return getTooltipValue(
              viewOption,
              tooltipItem.formattedValue,
              distanceUnit,
            );
          },
        },
        titleFont: {
          weight: 'normal',
        },
        padding: 10,
        titleAlign: 'center',
        bodyFont: {
          weight: 'bold',
        },
        bodyAlign: 'center',
        displayColors: false,
      },
    },
    scales: {
      x: {
        type: 'time',
        min,
        max,
        time: {
          unit: dateSegment === 'year' ? 'month' : 'day',
          round: dateSegment === 'year' ? 'month' : 'day',
          displayFormats: {
            day: dateSegment === 'month' ? 'd' : 'eeeee',
            month: 'MMM',
            year: 'YYYY',
          },
          tooltipFormat: getFormatForDateSegment(dateSegment),
        },
        stacked: true,
        ticks: {
          color: getTextColor(),
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        position: 'right',
        ticks: {
          stepSize: 1,
          color: getTextColor(),
        },
      },
    },
    responsive: true,
  };
  return options;
}

export function getStepViewLabel(viewOption: StepViewOption) {
  switch (viewOption) {
    case 'distance':
      return translate('DISTANCE');
    case 'steps':
      return translate('STEPS');
    default:
      return exhaustiveCheck(viewOption);
  }
}

export function getStepTotalLabel(viewOption: StepViewOption) {
  switch (viewOption) {
    case 'distance':
      return translate('TOTAL_DISTANCE');
    case 'steps':
      return translate('TOTAL_STEPS');
    default:
      return exhaustiveCheck(viewOption);
  }
}

export function getStepViewUnit(
  viewOption: StepViewOption,
  distanceUnit: SworkitState['user']['profile']['distanceUnit'],
) {
  switch (viewOption) {
    case 'distance':
      return translate(getDistanceUnitTranslationKey(distanceUnit));
    case 'steps':
      return translate('STEPS');
    default:
      return exhaustiveCheck(viewOption);
  }
}

export function getStepViewUnitChallengeTypeTranslation(
  viewOption: ChallengeDoc['challengeType'],
  distanceUnit: SworkitState['user']['profile']['distanceUnit'],
) {
  switch (viewOption) {
    case 'distance':
      return translate(getDistanceUnitTranslationKey(distanceUnit));
    case 'steps':
      return translate('STEPS');
    default:
      return null;
  }
}

export function getStepChartColor(viewOption: StepViewOption) {
  switch (viewOption) {
    case 'distance':
      return '#0a8ee6';
    case 'steps':
      return '#ef4852';
    default:
      return exhaustiveCheck(viewOption);
  }
}

function getFormatForDateSegment(dateSegment: DateSegment) {
  switch (dateSegment) {
    case 'week':
      return 'MMM d';
    case 'month':
      return 'MMM d';
    case 'year':
      return 'MMM';
    default:
      return exhaustiveCheck(dateSegment);
  }
}

export function formatDateRangeForDateSegment(
  startDate: Date,
  endDate: Date,
  dateUnit: 'week' | 'month' | 'year' = 'week',
) {
  switch (dateUnit) {
    case 'week':
      return `${formatDate(startDate, 'MMM d')} -
        ${formatDate(endDate, 'MMM d')}`;
    case 'month':
      return formatDate(startDate, 'MMMM');
    case 'year':
      return formatDate(startDate, 'yyyy');
    default:
      return exhaustiveCheck(dateUnit);
  }
}

function getTooltipValue(
  viewOption: StepViewOption,
  value: string,
  distanceUnit: SworkitState['user']['profile']['distanceUnit'],
) {
  switch (viewOption) {
    case 'distance':
      return (
        value +
        (distanceUnit === 'kilometers'
          ? translate('KILOMETERS_ABRV')
          : translate('MILES_ABRV'))
      );
    case 'steps':
      return value;
    default:
      return exhaustiveCheck(viewOption);
  }
}

export function getStepViewChartDataset(
  viewOption: StepViewOption,
  stepLogs: PartialStepLog[],
  distanceUnit: SworkitState['user']['profile']['distanceUnit'],
) {
  const dataset: ChartDataset = {
    label: getStepViewLabel(viewOption),
    backgroundColor: getStepChartColor(viewOption),
    borderColor: getStepChartColor(viewOption),
    fill: 'origin',
    data: stepLogs.map((stepLog) => ({
      x: getDateAtMidnightFromString(stepLog.stepDay)?.valueOf(),
      y: getStepViewValueFromLog(viewOption, stepLog, distanceUnit),
    })),
  };
  return [dataset];
}

export function getStepGoalDataset(
  stepLogs: PartialStepLog[],
  stepGoal: SworkitState['user']['profile']['dailyStepGoal'],
) {
  const dataset: ChartDataset = {
    label: translate('DAILY_STEP_GOAL'),
    backgroundColor: '#bbb',
    borderColor: '#bbb',
    borderDash: [5, 15],
    pointRadius: 0,
    spanGaps: true,
    type: 'line',
    data: stepLogs.map((stepLog) => ({
      x: getDateAtMidnightFromString(stepLog.stepDay)?.valueOf(),
      y: stepGoal || 10000,
    })),
  };
  return dataset;
}

export function stepLogIsFromToday(stepDay: string): boolean {
  return getTodayString() === stepDay;
}

export function lastSyncTime(lastSyncTimestampMillis: number): string {
  return formatDistanceToNow(new Date(lastSyncTimestampMillis), {
    addSuffix: true,
    locale: getDateFnsLocale(swApi.language),
  });
}

export function getTodaysLog(stepLogs: StepLog[]) {
  const stepLogToday = stepLogs?.find((stepLog) =>
    stepLogIsFromToday(stepLog.stepDay),
  );
  return stepLogToday;
}

export function getStepViewValueFromLog(
  viewOption: StepViewOption,
  log: PartialStepLog,
  distanceUnit: SworkitState['user']['profile']['distanceUnit'],
) {
  switch (viewOption) {
    case 'distance':
      return getConvertedDistanceForUnit(log.distanceMeters, distanceUnit);
    case 'steps':
      return log.stepCount || 0;
    default:
      return exhaustiveCheck(viewOption);
  }
}

export type PartialStepLog = Pick<
  StepLog,
  'stepCount' | 'distanceMeters' | 'stepDay'
>;
